/**
 * @file pages/MeditationCalendar.js
 * @description Calendrier de méditation avec rappels navigateur + email
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { API } from '../context/AuthContext';

const MONTHS_FR = ['Janvier','Février','Mars','Avril','Mai','Juin','Juillet','Août','Septembre','Octobre','Novembre','Décembre'];
const DAYS_FR = ['Lun','Mar','Mer','Jeu','Ven','Sam','Dim'];
const DURATIONS = [5,10,15,20,30,45,60,90,120];

// ─── Helpers ──────────────────────────────────────────────────────────────────
const pad = (n) => String(n).padStart(2, '0');

const formatTime = (date) => {
  const d = new Date(date);
  return `${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const formatFull = (date) => {
  const d = new Date(date);
  return d.toLocaleDateString('fr-FR', { weekday:'long', day:'numeric', month:'long', year:'numeric' }) + ' à ' + formatTime(d);
};

const isSameDay = (a, b) => {
  const da = new Date(a), db = new Date(b);
  return da.getFullYear() === db.getFullYear() && da.getMonth() === db.getMonth() && da.getDate() === db.getDate();
};

const isToday = (date) => isSameDay(date, new Date());
const isPast = (date) => new Date(date) < new Date();

// ─── Composant Modal ──────────────────────────────────────────────────────────
const Modal = ({ onClose, onSave, selectedDate, editData }) => {
  const defaultDate = selectedDate ? new Date(selectedDate) : new Date();
  const defaultDateStr = `${defaultDate.getFullYear()}-${pad(defaultDate.getMonth()+1)}-${pad(defaultDate.getDate())}`;
  const defaultTimeStr = `${pad(defaultDate.getHours())}:${pad(defaultDate.getMinutes())}`;

  const [form, setForm] = useState({
    title: editData?.title || '',
    note: editData?.note || '',
    duration: editData?.duration || 15,
    date: editData ? new Date(editData.scheduledAt).toISOString().slice(0,10) : defaultDateStr,
    time: editData ? formatTime(editData.scheduledAt) : defaultTimeStr,
    reminderBrowser: editData?.reminders?.browser ?? true,
    reminderEmail: editData?.reminders?.email ?? true,
  });
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  const validate = () => {
    const e = {};
    if (!form.title.trim()) e.title = 'Le titre est requis';
    if (!form.date) e.date = 'La date est requise';
    if (!form.time) e.time = "L'heure est requise";
    return e;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }
    setSaving(true);
    const scheduledAt = new Date(`${form.date}T${form.time}:00`).toISOString();
    await onSave({
      title: form.title.trim(),
      note: form.note.trim(),
      duration: Number(form.duration),
      scheduledAt,
      reminders: { browser: form.reminderBrowser, email: form.reminderEmail },
    });
    setSaving(false);
  };

  return (
    <div className="modal-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal-box">
        <div className="modal-header">
          <h2 className="modal-title">{editData ? 'Modifier la méditation' : 'Planifier une méditation'}</h2>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>

        <form onSubmit={handleSubmit}>
          {/* Titre */}
          <div className="mf-group">
            <label className="mf-label">Titre / Thème *</label>
            <input className={`mf-input ${errors.title ? 'error' : ''}`} type="text"
              placeholder="Ex: Méditation sur Romains 8..." maxLength={100}
              value={form.title} onChange={e => { setForm({...form, title: e.target.value}); setErrors({...errors, title:''}); }} />
            {errors.title && <span className="mf-error">{errors.title}</span>}
          </div>

          {/* Date + Heure */}
          <div className="mf-row">
            <div className="mf-group">
              <label className="mf-label">Date *</label>
              <input className={`mf-input ${errors.date ? 'error' : ''}`} type="date"
                value={form.date} onChange={e => { setForm({...form, date: e.target.value}); setErrors({...errors, date:''}); }} />
              {errors.date && <span className="mf-error">{errors.date}</span>}
            </div>
            <div className="mf-group">
              <label className="mf-label">Heure *</label>
              <input className={`mf-input ${errors.time ? 'error' : ''}`} type="time"
                value={form.time} onChange={e => { setForm({...form, time: e.target.value}); setErrors({...errors, time:''}); }} />
              {errors.time && <span className="mf-error">{errors.time}</span>}
            </div>
          </div>

          {/* Durée */}
          <div className="mf-group">
            <label className="mf-label">Durée estimée</label>
            <div className="duration-grid">
              {DURATIONS.map(d => (
                <button key={d} type="button"
                  className={`duration-btn ${form.duration === d ? 'active' : ''}`}
                  onClick={() => setForm({...form, duration: d})}>
                  {d < 60 ? `${d} min` : `${d/60}h`}
                </button>
              ))}
            </div>
          </div>

          {/* Note */}
          <div className="mf-group">
            <label className="mf-label">Note personnelle</label>
            <textarea className="mf-input mf-textarea"
              placeholder="Ce que vous souhaitez méditer, une intention..." maxLength={1000}
              value={form.note} onChange={e => setForm({...form, note: e.target.value})} />
            <span className="mf-char">{form.note.length}/1000</span>
          </div>

          {/* Rappels */}
          <div className="mf-group">
            <label className="mf-label">Rappels (30 min avant)</label>
            <div className="reminder-options">
              <label className="reminder-toggle">
                <input type="checkbox" checked={form.reminderBrowser}
                  onChange={e => setForm({...form, reminderBrowser: e.target.checked})} />
                <span className="toggle-track"><span className="toggle-thumb" /></span>
                <span className="toggle-label">🔔 Navigateur</span>
              </label>
              <label className="reminder-toggle">
                <input type="checkbox" checked={form.reminderEmail}
                  onChange={e => setForm({...form, reminderEmail: e.target.checked})} />
                <span className="toggle-track"><span className="toggle-thumb" /></span>
                <span className="toggle-label">✉️ Email</span>
              </label>
            </div>
          </div>

          <div className="modal-footer">
            <button type="button" className="btn-cancel" onClick={onClose}>Annuler</button>
            <button type="submit" className="btn-save" disabled={saving}>
              {saving ? 'Enregistrement...' : editData ? 'Modifier' : '✦ Planifier'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ─── Composant principal ──────────────────────────────────────────────────────
const MeditationCalendar = () => {
  const { isAuthenticated, user } = useAuth();
  const navigate = useNavigate();

  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
  const [currentMonth, setCurrentMonth] = useState(new Date().getMonth());
  const [view, setView] = useState('month'); // month | week | day
  const [meditations, setMeditations] = useState([]);
  const [selectedDay, setSelectedDay] = useState(new Date());
  const [showModal, setShowModal] = useState(false);
  const [editData, setEditData] = useState(null);
  const [modalDate, setModalDate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notifPermission, setNotifPermission] = useState('default');
  const reminderIntervalRef = useRef(null);

  useEffect(() => {
    if (!isAuthenticated) navigate('/login');
  }, [isAuthenticated, navigate]);

  // Vérifier permission notifications
  useEffect(() => {
    if ('Notification' in window) {
      setNotifPermission(Notification.permission);
    }
  }, []);

  // Charger les méditations du mois
  const fetchMeditations = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await API.get(`/meditation?year=${currentYear}&month=${currentMonth + 1}`);
      setMeditations(data.meditations);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [currentYear, currentMonth]);

  useEffect(() => {
    fetchMeditations();
  }, [fetchMeditations]);

  // Système de rappels navigateur (vérifie toutes les minutes)
  useEffect(() => {
    if (notifPermission !== 'granted') return;

    const checkReminders = () => {
      const now = new Date();
      meditations.forEach(m => {
        if (m.reminderSent || m.completed || !m.reminders?.browser) return;
        const diff = new Date(m.scheduledAt) - now;
        if (diff > 0 && diff <= 30 * 60 * 1000) {
          const mins = Math.round(diff / 60000);
          new Notification('⏰ Rappel de méditation — VerbeVivant', {
            body: `"${m.title}" commence dans ${mins} minute${mins > 1 ? 's' : ''}`,
            icon: '/favicon.ico',
            tag: m._id,
          });
          // Marquer comme envoyé en base
          API.patch(`/meditation/${m._id}/complete`, { completed: false })
            .catch(() => {});
        }
      });
    };

    reminderIntervalRef.current = setInterval(checkReminders, 60000);
    return () => clearInterval(reminderIntervalRef.current);
  }, [meditations, notifPermission]);

  const requestNotifPermission = async () => {
    const perm = await Notification.requestPermission();
    setNotifPermission(perm);
  };

  // Sauvegarder une méditation
  const handleSave = async (formData) => {
    try {
      if (editData) {
        await API.put(`/meditation/${editData._id}`, formData);
      } else {
        await API.post('/meditation', formData);
      }
      setShowModal(false);
      setEditData(null);
      fetchMeditations();
    } catch (err) {
      console.error(err);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cette méditation ?')) return;
    await API.delete(`/meditation/${id}`);
    fetchMeditations();
  };

  const handleToggleComplete = async (m) => {
    await API.patch(`/meditation/${m._id}/complete`, { completed: !m.completed });
    fetchMeditations();
  };

  const openNewModal = (date) => {
    setEditData(null);
    setModalDate(date || selectedDay);
    setShowModal(true);
  };

  const openEditModal = (m) => {
    setEditData(m);
    setShowModal(true);
  };

  // Navigation
  const prevMonth = () => {
    if (currentMonth === 0) { setCurrentMonth(11); setCurrentYear(y => y - 1); }
    else setCurrentMonth(m => m - 1);
  };
  const nextMonth = () => {
    if (currentMonth === 11) { setCurrentMonth(0); setCurrentYear(y => y + 1); }
    else setCurrentMonth(m => m + 1);
  };

  // Construire la grille du mois
  const buildMonthGrid = () => {
    const firstDay = new Date(currentYear, currentMonth, 1);
    const lastDay = new Date(currentYear, currentMonth + 1, 0);
    const startDow = (firstDay.getDay() + 6) % 7; // Lundi = 0
    const days = [];

    for (let i = 0; i < startDow; i++) {
      const d = new Date(currentYear, currentMonth, 1 - (startDow - i));
      days.push({ date: d, currentMonth: false });
    }
    for (let i = 1; i <= lastDay.getDate(); i++) {
      days.push({ date: new Date(currentYear, currentMonth, i), currentMonth: true });
    }
    const remaining = 42 - days.length;
    for (let i = 1; i <= remaining; i++) {
      days.push({ date: new Date(currentYear, currentMonth + 1, i), currentMonth: false });
    }
    return days;
  };

  const getMeditationsForDay = (date) =>
    meditations.filter(m => isSameDay(m.scheduledAt, date));

  const dayMeditations = getMeditationsForDay(selectedDay);
  const monthGrid = buildMonthGrid();

  // Stats du mois
  const totalMonth = meditations.length;
  const completedMonth = meditations.filter(m => m.completed).length;
  const upcomingMonth = meditations.filter(m => !m.completed && !isPast(m.scheduledAt)).length;

  return (
    <>
      <style>{`
        .cal-page {
          min-height: 100vh;
          padding: 80px 0 40px;
          background: linear-gradient(180deg, #1F2A44 0%, #141d30 100%);
        }
        .cal-container {
          max-width: 1300px;
          margin: 0 auto;
          padding: 32px 24px;
        }
        /* ─ Header ─ */
        .cal-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-bottom: 32px;
          flex-wrap: wrap;
          gap: 16px;
          animation: fadeInUp 0.5s ease;
        }
        .cal-header-left h1 {
          font-family: 'Cormorant Garamond', serif;
          font-size: clamp(1.8rem, 3vw, 2.5rem);
          color: #FFFFFF;
          line-height: 1.2;
        }
        .cal-header-left p {
          color: rgba(240,234,226,0.5);
          font-size: 0.9rem;
          margin-top: 4px;
        }
        .cal-header-actions { display: flex; gap: 10px; align-items: center; flex-wrap: wrap; }
        .notif-btn {
          display: flex;
          align-items: center;
          gap: 6px;
          background: rgba(169,134,103,0.12);
          border: 1px solid rgba(169,134,103,0.25);
          border-radius: 50px;
          padding: 8px 16px;
          color: rgba(240,234,226,0.7);
          font-size: 0.82rem;
          cursor: pointer;
          transition: all 0.2s;
          font-family: 'Jost', sans-serif;
        }
        .notif-btn.granted { color: #27ae60; border-color: rgba(39,174,96,0.3); }
        .notif-btn:hover { border-color: #A98667; color: #FFFFFF; }
        .add-btn {
          display: flex;
          align-items: center;
          gap: 8px;
          background: linear-gradient(135deg, #A98667, #c4a07d);
          border: none;
          border-radius: 50px;
          padding: 10px 22px;
          color: #FFFFFF;
          font-size: 0.9rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
          font-family: 'Jost', sans-serif;
        }
        .add-btn:hover { transform: translateY(-2px); box-shadow: 0 8px 24px rgba(169,134,103,0.4); }

        /* ─ Stats ─ */
        .cal-stats {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 16px;
          margin-bottom: 28px;
          animation: fadeInUp 0.5s ease 0.1s both;
        }
        .stat-card {
          background: rgba(31,42,68,0.5);
          border: 1px solid rgba(169,134,103,0.12);
          border-radius: 14px;
          padding: 18px 20px;
          display: flex;
          align-items: center;
          gap: 14px;
        }
        .stat-icon {
          width: 44px; height: 44px;
          background: rgba(169,134,103,0.12);
          border-radius: 10px;
          display: flex; align-items: center; justify-content: center;
          font-size: 1.2rem; flex-shrink: 0;
        }
        .stat-number {
          font-family: 'Cormorant Garamond', serif;
          font-size: 1.8rem; font-weight: 700; color: #A98667; line-height: 1;
        }
        .stat-label { font-size: 0.78rem; color: rgba(240,234,226,0.45); margin-top: 2px; }

        /* ─ Layout principal ─ */
        .cal-layout {
          display: grid;
          grid-template-columns: 1fr 340px;
          gap: 24px;
          animation: fadeInUp 0.5s ease 0.15s both;
        }

        /* ─ Calendrier ─ */
        .calendar-card {
          background: rgba(31,42,68,0.5);
          border: 1px solid rgba(169,134,103,0.15);
          border-radius: 20px;
          overflow: hidden;
        }
        .cal-nav {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 20px 24px;
          border-bottom: 1px solid rgba(169,134,103,0.1);
        }
        .cal-nav-btn {
          width: 36px; height: 36px;
          background: rgba(169,134,103,0.1);
          border: 1px solid rgba(169,134,103,0.2);
          border-radius: 8px;
          color: rgba(240,234,226,0.7);
          font-size: 1rem;
          cursor: pointer;
          transition: all 0.2s;
          display: flex; align-items: center; justify-content: center;
        }
        .cal-nav-btn:hover { background: rgba(169,134,103,0.2); color: #FFFFFF; }
        .cal-nav-center {
          display: flex; align-items: center; gap: 12px;
        }
        .cal-month-title {
          font-family: 'Cormorant Garamond', serif;
          font-size: 1.4rem; color: #FFFFFF; font-weight: 600;
        }
        .cal-year-select {
          background: rgba(169,134,103,0.1);
          border: 1px solid rgba(169,134,103,0.2);
          border-radius: 8px;
          color: #A98667;
          padding: 4px 10px;
          font-size: 0.875rem;
          font-family: 'Jost', sans-serif;
          cursor: pointer;
          outline: none;
        }
        .cal-today-btn {
          background: none;
          border: 1px solid rgba(169,134,103,0.2);
          border-radius: 50px;
          color: rgba(240,234,226,0.5);
          padding: 4px 12px;
          font-size: 0.78rem;
          cursor: pointer;
          transition: all 0.2s;
          font-family: 'Jost', sans-serif;
        }
        .cal-today-btn:hover { color: #A98667; border-color: #A98667; }

        /* Grille jours */
        .cal-days-header {
          display: grid;
          grid-template-columns: repeat(7, 1fr);
          padding: 12px 16px 8px;
        }
        .cal-day-name {
          text-align: center;
          font-size: 0.72rem;
          font-weight: 700;
          letter-spacing: 1px;
          text-transform: uppercase;
          color: rgba(240,234,226,0.3);
          padding: 4px 0;
        }
        .cal-grid {
          display: grid;
          grid-template-columns: repeat(7, 1fr);
          padding: 0 16px 16px;
          gap: 3px;
        }
        .cal-cell {
          min-height: 80px;
          border-radius: 10px;
          padding: 6px;
          cursor: pointer;
          transition: all 0.2s ease;
          position: relative;
          border: 1px solid transparent;
        }
        .cal-cell:hover { background: rgba(169,134,103,0.08); border-color: rgba(169,134,103,0.15); }
        .cal-cell.other-month { opacity: 0.35; }
        .cal-cell.today { border-color: rgba(169,134,103,0.35); background: rgba(169,134,103,0.06); }
        .cal-cell.selected { background: rgba(169,134,103,0.15); border-color: #A98667; }
        .cal-cell-day {
          font-size: 0.82rem;
          font-weight: 600;
          color: rgba(240,234,226,0.6);
          margin-bottom: 4px;
          width: 24px; height: 24px;
          display: flex; align-items: center; justify-content: center;
          border-radius: 50%;
        }
        .cal-cell.today .cal-cell-day {
          background: #A98667;
          color: #FFFFFF;
        }
        .cal-cell.selected:not(.today) .cal-cell-day {
          color: #FFFFFF;
        }
        .cal-event-dot {
          display: flex;
          align-items: center;
          gap: 3px;
          padding: 2px 5px;
          border-radius: 4px;
          font-size: 0.65rem;
          font-weight: 600;
          margin-bottom: 2px;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          max-width: 100%;
        }
        .cal-event-dot.pending {
          background: rgba(169,134,103,0.2);
          color: #c4a07d;
        }
        .cal-event-dot.completed {
          background: rgba(39,174,96,0.15);
          color: #27ae60;
          text-decoration: line-through;
          opacity: 0.7;
        }
        .cal-event-dot.past {
          background: rgba(231,76,60,0.12);
          color: rgba(231,76,60,0.7);
        }
        .cal-more {
          font-size: 0.62rem;
          color: #A98667;
          font-weight: 600;
          padding: 1px 4px;
        }

        /* ─ Panneau jour ─ */
        .day-panel {
          background: rgba(31,42,68,0.5);
          border: 1px solid rgba(169,134,103,0.15);
          border-radius: 20px;
          display: flex;
          flex-direction: column;
          overflow: hidden;
        }
        .day-panel-header {
          padding: 20px 20px 16px;
          border-bottom: 1px solid rgba(169,134,103,0.1);
        }
        .day-panel-date {
          font-family: 'Cormorant Garamond', serif;
          font-size: 1.3rem;
          color: #FFFFFF;
          text-transform: capitalize;
        }
        .day-panel-sub {
          font-size: 0.78rem;
          color: rgba(240,234,226,0.4);
          margin-top: 4px;
        }
        .day-panel-add {
          display: flex;
          align-items: center;
          gap: 6px;
          background: rgba(169,134,103,0.12);
          border: 1px dashed rgba(169,134,103,0.3);
          border-radius: 10px;
          padding: 10px 14px;
          color: rgba(240,234,226,0.6);
          font-size: 0.85rem;
          cursor: pointer;
          transition: all 0.2s;
          width: 100%;
          margin-top: 12px;
          font-family: 'Jost', sans-serif;
        }
        .day-panel-add:hover { background: rgba(169,134,103,0.2); color: #FFFFFF; border-style: solid; }
        .day-panel-list {
          flex: 1;
          overflow-y: auto;
          padding: 12px 16px;
        }
        .day-panel-list::-webkit-scrollbar { width: 3px; }
        .day-panel-list::-webkit-scrollbar-thumb { background: rgba(169,134,103,0.2); border-radius: 2px; }
        .meditation-item {
          background: rgba(255,255,255,0.03);
          border: 1px solid rgba(169,134,103,0.12);
          border-radius: 12px;
          padding: 14px;
          margin-bottom: 10px;
          transition: all 0.2s;
          position: relative;
        }
        .meditation-item:hover { border-color: rgba(169,134,103,0.3); }
        .meditation-item.completed { opacity: 0.6; }
        .med-item-header {
          display: flex;
          align-items: flex-start;
          justify-content: space-between;
          gap: 8px;
          margin-bottom: 8px;
        }
        .med-item-title {
          font-size: 0.9rem;
          font-weight: 600;
          color: #FFFFFF;
          line-height: 1.3;
          flex: 1;
        }
        .meditation-item.completed .med-item-title { text-decoration: line-through; color: rgba(240,234,226,0.5); }
        .med-item-actions {
          display: flex;
          gap: 4px;
          flex-shrink: 0;
        }
        .med-action-btn {
          width: 28px; height: 28px;
          border-radius: 6px;
          border: none;
          cursor: pointer;
          display: flex; align-items: center; justify-content: center;
          font-size: 0.8rem;
          transition: all 0.15s;
        }
        .med-action-btn.check {
          background: rgba(39,174,96,0.15);
          color: #27ae60;
        }
        .med-action-btn.check:hover { background: rgba(39,174,96,0.3); }
        .med-action-btn.check.active { background: #27ae60; color: #fff; }
        .med-action-btn.edit { background: rgba(169,134,103,0.15); color: #A98667; }
        .med-action-btn.edit:hover { background: rgba(169,134,103,0.3); }
        .med-action-btn.del { background: rgba(231,76,60,0.12); color: rgba(231,76,60,0.7); }
        .med-action-btn.del:hover { background: rgba(231,76,60,0.25); color: #e74c3c; }
        .med-item-meta {
          display: flex;
          gap: 10px;
          flex-wrap: wrap;
        }
        .med-meta-chip {
          display: flex;
          align-items: center;
          gap: 4px;
          font-size: 0.72rem;
          color: rgba(240,234,226,0.45);
          background: rgba(255,255,255,0.04);
          border-radius: 50px;
          padding: 2px 8px;
        }
        .med-meta-chip.time { color: #A98667; }
        .med-item-note {
          margin-top: 8px;
          font-size: 0.8rem;
          color: rgba(240,234,226,0.5);
          line-height: 1.5;
          font-style: italic;
          border-top: 1px solid rgba(169,134,103,0.08);
          padding-top: 8px;
        }
        .reminder-chips {
          display: flex;
          gap: 6px;
          margin-top: 6px;
        }
        .reminder-chip {
          font-size: 0.65rem;
          padding: 2px 7px;
          border-radius: 50px;
          background: rgba(169,134,103,0.1);
          color: rgba(240,234,226,0.4);
        }
        .no-med {
          text-align: center;
          padding: 32px 16px;
          color: rgba(240,234,226,0.3);
          font-size: 0.875rem;
        }
        .no-med-icon { font-size: 2rem; margin-bottom: 8px; }

        /* ─ Modal ─ */
        .modal-overlay {
          position: fixed; inset: 0; z-index: 2000;
          background: rgba(0,0,0,0.6);
          backdrop-filter: blur(4px);
          display: flex; align-items: center; justify-content: center;
          padding: 24px;
          animation: fadeIn 0.2s ease;
        }
        .modal-box {
          background: #1F2A44;
          border: 1px solid rgba(169,134,103,0.25);
          border-radius: 20px;
          width: 100%; max-width: 520px;
          max-height: 90vh;
          overflow-y: auto;
          animation: fadeInUp 0.3s ease;
        }
        .modal-box::-webkit-scrollbar { width: 4px; }
        .modal-box::-webkit-scrollbar-thumb { background: rgba(169,134,103,0.2); }
        .modal-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 24px 28px 0;
          margin-bottom: 24px;
        }
        .modal-title {
          font-family: 'Cormorant Garamond', serif;
          font-size: 1.5rem; color: #FFFFFF;
        }
        .modal-close {
          width: 32px; height: 32px;
          background: rgba(255,255,255,0.06);
          border: none; border-radius: 8px;
          color: rgba(240,234,226,0.6);
          cursor: pointer; font-size: 0.9rem;
          transition: all 0.2s;
        }
        .modal-close:hover { background: rgba(231,76,60,0.15); color: #e74c3c; }
        .modal-box form { padding: 0 28px 28px; }
        .mf-group { margin-bottom: 18px; }
        .mf-row { display: grid; grid-template-columns: 1fr 1fr; gap: 14px; }
        .mf-label {
          display: block;
          font-size: 0.8rem; font-weight: 600;
          color: rgba(240,234,226,0.6);
          margin-bottom: 7px; letter-spacing: 0.3px;
        }
        .mf-input {
          width: 100%;
          background: rgba(255,255,255,0.04);
          border: 1px solid rgba(169,134,103,0.2);
          border-radius: 10px;
          padding: 12px 14px;
          color: #FFFFFF;
          font-size: 0.9rem;
          font-family: 'Jost', sans-serif;
          outline: none;
          transition: all 0.2s;
        }
        .mf-input:focus { border-color: #A98667; background: rgba(169,134,103,0.06); }
        .mf-input.error { border-color: #e74c3c; }
        .mf-input::placeholder { color: rgba(240,234,226,0.25); }
        input[type="date"].mf-input, input[type="time"].mf-input { color-scheme: dark; }
        .mf-textarea { resize: vertical; min-height: 80px; line-height: 1.5; }
        .mf-char { font-size: 0.72rem; color: rgba(240,234,226,0.3); text-align: right; display: block; margin-top: 4px; }
        .mf-error { color: #e74c3c; font-size: 0.78rem; margin-top: 4px; display: block; }
        .duration-grid { display: flex; flex-wrap: wrap; gap: 8px; }
        .duration-btn {
          background: rgba(255,255,255,0.04);
          border: 1px solid rgba(169,134,103,0.2);
          border-radius: 8px;
          padding: 7px 12px;
          color: rgba(240,234,226,0.6);
          font-size: 0.8rem;
          cursor: pointer;
          transition: all 0.2s;
          font-family: 'Jost', sans-serif;
        }
        .duration-btn.active { background: rgba(169,134,103,0.2); border-color: #A98667; color: #FFFFFF; }
        .duration-btn:hover:not(.active) { border-color: rgba(169,134,103,0.4); color: #FFFFFF; }
        .reminder-options { display: flex; gap: 20px; flex-wrap: wrap; }
        .reminder-toggle {
          display: flex; align-items: center; gap: 10px;
          cursor: pointer; user-select: none;
        }
        .reminder-toggle input { display: none; }
        .toggle-track {
          width: 40px; height: 22px;
          background: rgba(255,255,255,0.08);
          border-radius: 11px;
          position: relative;
          transition: background 0.3s;
          flex-shrink: 0;
        }
        .reminder-toggle input:checked + .toggle-track { background: #A98667; }
        .toggle-thumb {
          position: absolute;
          top: 3px; left: 3px;
          width: 16px; height: 16px;
          background: #FFFFFF;
          border-radius: 50%;
          transition: transform 0.3s;
        }
        .reminder-toggle input:checked + .toggle-track .toggle-thumb { transform: translateX(18px); }
        .toggle-label { font-size: 0.875rem; color: rgba(240,234,226,0.7); }
        .modal-footer {
          display: flex; justify-content: flex-end; gap: 10px;
          margin-top: 24px; padding-top: 20px;
          border-top: 1px solid rgba(169,134,103,0.1);
        }
        .btn-cancel {
          background: none; border: 1px solid rgba(169,134,103,0.2);
          border-radius: 10px; padding: 11px 20px;
          color: rgba(240,234,226,0.6); font-size: 0.875rem;
          cursor: pointer; transition: all 0.2s;
          font-family: 'Jost', sans-serif;
        }
        .btn-cancel:hover { border-color: rgba(169,134,103,0.4); color: #FFFFFF; }
        .btn-save {
          background: linear-gradient(135deg, #A98667, #c4a07d);
          border: none; border-radius: 10px;
          padding: 11px 24px; color: #FFFFFF;
          font-size: 0.875rem; font-weight: 600;
          cursor: pointer; transition: all 0.3s;
          font-family: 'Jost', sans-serif;
        }
        .btn-save:hover:not(:disabled) { transform: translateY(-1px); box-shadow: 0 6px 20px rgba(169,134,103,0.35); }
        .btn-save:disabled { opacity: 0.6; cursor: not-allowed; }

        /* Responsive */
        @media (max-width: 1000px) {
          .cal-layout { grid-template-columns: 1fr; }
          .cal-stats { grid-template-columns: repeat(3, 1fr); }
        }
        @media (max-width: 600px) {
          .cal-container { padding: 20px 16px; }
          .cal-stats { grid-template-columns: 1fr; }
          .cal-cell { min-height: 60px; }
          .mf-row { grid-template-columns: 1fr; }
        }
      `}</style>

      <div className="cal-page">
        <div className="cal-container">

          {/* ── Header ── */}
          <div className="cal-header">
            <div className="cal-header-left">
              <h1>📅 Calendrier de Méditation</h1>
              <p>Planifiez vos moments de prière et de méditation biblique</p>
            </div>
            <div className="cal-header-actions">
              {/* Bouton notifications */}
              {'Notification' in window && (
                <button
                  className={`notif-btn ${notifPermission === 'granted' ? 'granted' : ''}`}
                  onClick={notifPermission !== 'granted' ? requestNotifPermission : undefined}
                >
                  {notifPermission === 'granted' ? '🔔 Rappels activés' : '🔕 Activer les rappels'}
                </button>
              )}
              <button className="add-btn" onClick={() => openNewModal(selectedDay)}>
                + Planifier
              </button>
            </div>
          </div>

          {/* ── Stats ── */}
          <div className="cal-stats">
            {[
              { icon: '📅', number: totalMonth, label: `Méditation${totalMonth !== 1 ? 's' : ''} ce mois` },
              { icon: '✅', number: completedMonth, label: 'Complétées' },
              { icon: '⏳', number: upcomingMonth, label: 'À venir' },
            ].map((s, i) => (
              <div className="stat-card" key={i}>
                <div className="stat-icon">{s.icon}</div>
                <div>
                  <div className="stat-number">{s.number}</div>
                  <div className="stat-label">{s.label}</div>
                </div>
              </div>
            ))}
          </div>

          {/* ── Layout calendrier + panneau ── */}
          <div className="cal-layout">

            {/* Calendrier mensuel */}
            <div className="calendar-card">
              <div className="cal-nav">
                <button className="cal-nav-btn" onClick={prevMonth}>‹</button>
                <div className="cal-nav-center">
                  <button className="cal-today-btn" onClick={() => {
                    const now = new Date();
                    setCurrentYear(now.getFullYear());
                    setCurrentMonth(now.getMonth());
                    setSelectedDay(now);
                  }}>Aujourd'hui</button>
                  <span className="cal-month-title">{MONTHS_FR[currentMonth]}</span>
                  <select className="cal-year-select" value={currentYear}
                    onChange={e => setCurrentYear(Number(e.target.value))}>
                    {Array.from({ length: 10 }, (_, i) => new Date().getFullYear() - 2 + i).map(y => (
                      <option key={y} value={y}>{y}</option>
                    ))}
                  </select>
                </div>
                <button className="cal-nav-btn" onClick={nextMonth}>›</button>
              </div>

              <div className="cal-days-header">
                {DAYS_FR.map(d => <div key={d} className="cal-day-name">{d}</div>)}
              </div>

              <div className="cal-grid">
                {monthGrid.map(({ date, currentMonth: inMonth }, i) => {
                  const dayMeds = getMeditationsForDay(date);
                  const isSelected = isSameDay(date, selectedDay);
                  const isTodayCell = isToday(date);
                  return (
                    <div
                      key={i}
                      className={`cal-cell ${!inMonth ? 'other-month' : ''} ${isTodayCell ? 'today' : ''} ${isSelected ? 'selected' : ''}`}
                      onClick={() => setSelectedDay(new Date(date))}
                      onDoubleClick={() => { setSelectedDay(new Date(date)); openNewModal(new Date(date)); }}
                      title="Clic: voir — Double-clic: ajouter"
                    >
                      <div className="cal-cell-day">{date.getDate()}</div>
                      {dayMeds.slice(0, 2).map(m => (
                        <div key={m._id}
                          className={`cal-event-dot ${m.completed ? 'completed' : isPast(m.scheduledAt) ? 'past' : 'pending'}`}>
                          {formatTime(m.scheduledAt)} {m.title}
                        </div>
                      ))}
                      {dayMeds.length > 2 && (
                        <div className="cal-more">+{dayMeds.length - 2}</div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Panneau du jour sélectionné */}
            <div className="day-panel">
              <div className="day-panel-header">
                <div className="day-panel-date">
                  {selectedDay.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' })}
                </div>
                <div className="day-panel-sub">
                  {isToday(selectedDay) ? "Aujourd'hui" : ''}
                  {dayMeditations.length > 0 ? ` · ${dayMeditations.length} méditation${dayMeditations.length > 1 ? 's' : ''}` : ' · Aucune méditation'}
                </div>
                <button className="day-panel-add" onClick={() => openNewModal(selectedDay)}>
                  + Planifier une méditation ce jour
                </button>
              </div>

              <div className="day-panel-list">
                {loading ? (
                  <div style={{ display: 'flex', justifyContent: 'center', padding: 24 }}>
                    <div className="loader" style={{ width: 24, height: 24, borderWidth: 2 }} />
                  </div>
                ) : dayMeditations.length === 0 ? (
                  <div className="no-med">
                    <div className="no-med-icon">🙏</div>
                    <p>Aucune méditation planifiée</p>
                    <p style={{ fontSize: '0.78rem', marginTop: 4, opacity: 0.6 }}>Double-cliquez sur le calendrier ou utilisez le bouton ci-dessus</p>
                  </div>
                ) : (
                  dayMeditations
                    .sort((a, b) => new Date(a.scheduledAt) - new Date(b.scheduledAt))
                    .map(m => (
                      <div key={m._id} className={`meditation-item ${m.completed ? 'completed' : ''}`}>
                        <div className="med-item-header">
                          <div className="med-item-title">{m.title}</div>
                          <div className="med-item-actions">
                            <button
                              className={`med-action-btn check ${m.completed ? 'active' : ''}`}
                              onClick={() => handleToggleComplete(m)}
                              title={m.completed ? 'Marquer incomplet' : 'Marquer complété'}
                            >✓</button>
                            <button className="med-action-btn edit" onClick={() => openEditModal(m)} title="Modifier">✎</button>
                            <button className="med-action-btn del" onClick={() => handleDelete(m._id)} title="Supprimer">✕</button>
                          </div>
                        </div>
                        <div className="med-item-meta">
                          <span className="med-meta-chip time">⏰ {formatTime(m.scheduledAt)}</span>
                          <span className="med-meta-chip">⏱ {m.duration < 60 ? `${m.duration} min` : `${m.duration/60}h`}</span>
                          {m.completed && <span className="med-meta-chip" style={{ color: '#27ae60' }}>✓ Complétée</span>}
                          {!m.completed && isPast(m.scheduledAt) && <span className="med-meta-chip" style={{ color: 'rgba(231,76,60,0.7)' }}>Passée</span>}
                        </div>
                        {m.note && <div className="med-item-note">{m.note}</div>}
                        <div className="reminder-chips">
                          {m.reminders?.browser && <span className="reminder-chip">🔔 Navigateur</span>}
                          {m.reminders?.email && <span className="reminder-chip">✉️ Email</span>}
                        </div>
                      </div>
                    ))
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modal */}
      {showModal && (
        <Modal
          onClose={() => { setShowModal(false); setEditData(null); }}
          onSave={handleSave}
          selectedDate={modalDate || selectedDay}
          editData={editData}
        />
      )}
    </>
  );
};

export default MeditationCalendar;